import { faEye, faKey, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { UserRole, UserViewModel } from 'stageclip-common';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

import { createModalOpenAction } from '../../actions/modalActions';
import { createRequestAllUsersAction } from '../../actions/userActions';
import { ApiDataTypes } from '../../api/apiDataTypes';
import { FormId } from '../../constants/formId';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import { getAll } from '../../store/selectors';
import Button from '../buttons/button';
import {
  BaseDispatchProps,
  baseMapDispatchToProps,
  baseMapStateToProps,
  BaseStateProps,
  ComponentBase,
} from '../componentBase';
import { ActionLogModal } from '../actionLogModal/ActionLogModal';

import { fetchUserActions } from 'api/clients/clients.actions';
import { Response } from 'helpers/callApi/callApi.types';
import { AppTable, AppTableColumn } from 'components/table/appTable';
import { SimpleModalId } from 'constants/modalId';
import { DeleteUserModal } from 'components/deleteUserModal/DeleteUserModal';
import { EditUserForm } from './editUserForm';

interface UsersTableStateProps extends BaseStateProps {
  allUsersLoaded: boolean;
  users: UserViewModel[];
}

interface UsersTableDispatchProps extends BaseDispatchProps {
  loadAllUsers: (c: UsersTable) => void;
  openEditUserForm: (userData: UserViewModel) => void;
  openUserActionLog: (userData: UserViewModel) => void;
  getUserLogs: (userId: string) => Response;
}

class UsersTable extends ComponentBase<{}, UsersTableStateProps, UsersTableDispatchProps> {
  private columns: AppTableColumn[] = [
    {
      Header: 'First Name',
      accessor: (user: UserViewModel) => user.firstName,
      id: 'firstName',
    },
    {
      Header: 'Last Name',
      accessor: (user: UserViewModel) => user.lastName,
      id: 'lastName',
    },
    {
      Header: 'Email',
      accessor: (user: UserViewModel) => user.email,
      id: 'email',
    },
    {
      Header: 'Role',
      accessor: (user: UserViewModel) => user.role,
      dropdownFilter: true,
      id: 'role',
    },
    {
      Cell: (row: any) => (
        <Link to={`/users/${row.original.id}/change-password`}>
          <Button className="button" text="Change Password" icon={faKey} slimButton />
        </Link>
      ),
      Header: 'Change Password',
      accessor: () => 'Change Password',
      id: 'changePassword',
    },
    {
      Cell: (row: any) => this.renderEditUserButton(row.original),
      Header: 'Edit',
      accessor: (user: UserViewModel) => user,
      id: 'edit',
      width: 150,
    },
    {
      Cell: (row: any) => this.renderOpenUserActionLog(row.original),
      Header: 'Action log',
      accessor: (user: UserViewModel) => user,
      id: 'edit',
      width: 150,
    },
  ];

  protected dataLoaded(props: any) {
    return props.allUsersLoaded;
  }

  protected async loadData(props: any) {
    await props.loadAllUsers(this);
  }

  protected renderEditUserButton(userData: UserViewModel) {
    return <Button text="Edit" icon={faPencilAlt} slimButton onClick={this.openEditUserForm(userData)} />;
  }

  renderOpenUserActionLog(userData: UserViewModel) {
    return <Button text="Open log" icon={faEye} slimButton onClick={() => this.props.openUserActionLog(userData)} />;
  }

  protected openEditUserForm(userData: UserViewModel) {
    const callback = () => this.props.openEditUserForm(userData);

    return callback.bind(this);
  }

  protected getClippingPlatformUsers = () => this.props.users.filter(({ role }) => role !== UserRole.User);

  protected getClientPortalUsers = () => this.props.users.filter(({ role }) => role === UserRole.User);

  protected loggedInRender() {
    return (
      <>
        <Tabs>
          <TabList>
            <Tab>Clipping Platform</Tab>
            <Tab>Client Portal</Tab>
          </TabList>
          <TabPanel>
            <AppTable
              columns={this.columns}
              tableName="users-clipping-platform"
              data={this.getClippingPlatformUsers()}
            />
          </TabPanel>
          <TabPanel>
            <AppTable columns={this.columns} tableName="users-client-portal" data={this.getClientPortalUsers()} />
          </TabPanel>
        </Tabs>
        <EditUserForm />
        <DeleteUserModal />
        <ActionLogModal getData={this.props.getUserLogs} type="User" />
      </>
    );
  }
}

function mapStateToProps(state: ClippingPlatformState): UsersTableStateProps {
  return {
    ...baseMapStateToProps(state),
    allUsersLoaded: state.appStatus.allLoaded[ApiDataTypes.Users],
    users: getAll(state.entities.users),
  };
}

function mapDispatchToProps(dispatch: Dispatch): UsersTableDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    loadAllUsers: (c: UsersTable) => dispatch(createRequestAllUsersAction(c, dispatch)),
    openEditUserForm: (userData: UserViewModel) =>
      dispatch(
        createModalOpenAction(FormId.EditUserForm, {
          ...userData,
          userId: userData.id,
        }),
      ),
    openUserActionLog: (userData: UserViewModel) =>
      dispatch(
        createModalOpenAction(SimpleModalId.ActionLog, {
          fetchDataId: userData.id,
          name: `${userData.firstName} ${userData.lastName}`,
        }),
      ),
    // @ts-ignore
    getUserLogs: (userId: string) => dispatch(fetchUserActions(userId)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UsersTable);
